import animate from '@play-co/timestep-core/lib/animate';
import View from '@play-co/timestep-core/lib/ui/View';
import ButtonView from '@play-co/timestep-core/lib/ui/widget/ButtonView';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import { CloseButtonType } from 'src/lib/ui/config/buttons';
import { createEmitter } from 'src/lib/Emitter';
import { animDuration } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import PopupBasicTitle, { PopupBasicTitleType } from './PopupBasicTitle';
import { trackFirstButtonTap } from 'src/lib/analytics/firstButtonTap';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';

// TODO: Typing #init with this type causes every class inheriting from PopupBasic to raise type errors.
export type PopupBasicOpts = {
  title?: string;
  message?: string;
};

export default abstract class PopupBasic<
  CloseHandler extends Function = () => void
> {
  protected root: View;

  protected bg: ButtonView;
  protected box: View | ImageScaleView;
  protected title: PopupBasicTitle;
  protected message: LangBitmapFontTextView;
  protected buttonClose: ButtonScaleView;
  protected overlay: View;

  // Scale that the popup view will end up after show up animation (fadeIn)
  protected viewScale = 1;

  constructor(
    protected opts: {
      superview: View;
      // The only reason to pass the PopupID around is as a shorthand for closing the popup.
      // We pass the shorthand directly instead. This increases type-safety around `CloseArgs`.
      close: CloseHandler;
      width?: number;
      height?: number;
      offsetY?: number;
      zIndex?: number;
      boxType?: 'normal' | 'small';
      boxOpts?: ImageScaleViewOpts;
      closeableWithBg?: boolean;
      darkerBg?: boolean;
      skipBlackBorder?: boolean;
      skipTitle?: boolean;
      skipMessage?: boolean;
      titleBannerType?: PopupBasicTitleType;
      closeButtonType?: CloseButtonType;
      tracking: { feature: string; $subFeature: string };
      titleIcon?: ImageScaleViewOpts;
    },
  ) {
    this.root = new View({
      width: opts.superview.style.width,
      height: opts.superview.style.height,
      zIndex: opts.zIndex || 9998,
      infinite: true,
    });

    const bgType = opts.darkerBg
      ? uiConfig.popups.backgroundDarker
      : uiConfig.popups.background;

    this.bg = new ButtonView({
      ...bgType,
      superview: this.root,
      onClick: () => {
        if (opts.closeableWithBg) {
          trackFirstButtonTap({
            feature: this.opts.tracking.feature,
            $subFeature: `${this.opts.tracking.$subFeature}-basic-background`,
          });
          opts.close();
        }
      },
    });

    this.box = new ImageScaleView({
      ...(opts.boxOpts ? opts.boxOpts : uiConfig.popups.buruburu),
      superview: this.root,
      infinite: true,
      canHandleEvents: false,
      width: opts.width || 591,
      height: opts.height || 903,
      x: this.root.style.width * 0.5,
      y: this.root.style.height * 0.5 + (opts.offsetY || 0),
      centerOnOrigin: true,
      centerAnchor: true,
    });

    if (opts.boxType === 'normal') {
      if (this.box.style.width < uiConfig.popups.minimumWidth) {
        throw Error('Popup below minimum width!');
      }

      if (this.box.style.height < uiConfig.popups.minimumHeight) {
        throw Error('Popup below minimum height!');
      }
    }

    this.overlay = new View({
      superview: this.root,
      infinite: true,
      zIndex: 10000,
      visible: false,
    });

    if (!opts.skipTitle) {
      this.title = new PopupBasicTitle({
        superview: this.box,
        type: opts.titleBannerType,
        icon: opts.titleIcon,
      });
    }

    if (!opts.skipMessage) {
      this.message = new LangBitmapFontTextView({
        superview: this.box,
        x: 40,
        y: 55,
        width: this.box.style.width - 80,
        align: 'center',
        verticalAlign: 'center',
        size: 26,
        color: '#BF8147',
        wordWrap: true,
        font: bitmapFonts('Body'),
        isRichText: true,
        canHandleEvents: false,
      });
    }

    const sourceCloseButton = opts.closeButtonType
      ? opts.closeButtonType === 'alt'
        ? uiConfig.buttons.closeAlt
        : uiConfig.buttons.close
      : !opts.skipBlackBorder && opts.boxType === 'small'
      ? uiConfig.buttons.close
      : uiConfig.buttons.closeAlt;
    this.buttonClose = new ButtonScaleView({
      superview: this.box,
      ...sourceCloseButton,
      x: this.box.style.width - 24,
      y: 24,
      onClick: async () => opts.close(),
      tracking: {
        feature: this.opts.tracking.feature,
        $subFeature: `${this.opts.tracking.$subFeature}-basic-close`,
      },
    });

    // anchor elements
    createEmitter(this.root, ({ ui }) => ui.screenSize).addListener(
      (screen) => {
        this.bg.updateOpts({
          width: screen.width,
          height: screen.height,
          opacity: 1,
        });
      },
    );
  }

  getView(): View {
    return this.root;
  }

  getBox(): View {
    return this.box;
  }

  getButtonClose(): View {
    return this.buttonClose;
  }

  init(opts: any) {
    const { title, message, titleIcon } = opts;
    if (this.title) {
      this.title.setText(() => title || '');
      titleIcon && this.title.setIcon(titleIcon);
    }
    if (this.message) this.message.localeText = () => message || '';

    this.fadeIn();
  }

  fadeIn() {
    this.overlay.show();
    this.attachRoot();

    this.bg.updateOpts({ opacity: 0 });
    animate(this.bg)
      .clear()
      .then({ opacity: 1 }, animDuration, animate.easeOut);

    this.box.updateOpts({ scale: 0 });
    animate(this.box)
      .clear()
      .wait(animDuration)
      .then({ scale: this.viewScale }, animDuration, animate.easeOut)
      .then(() => this.overlay.hide());
  }

  fadeOut() {
    this.overlay.show();

    animate(this.bg)
      .clear()
      .wait(animDuration)
      .then({ opacity: 0 }, animDuration, animate.easeOut)
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });

    animate(this.box)
      .clear()
      .wait(0)
      .then({ scale: 0 }, animDuration, animate.easeOut);
  }

  //

  protected attachRoot() {
    this.opts.superview.addSubview(this.root);
  }

  protected detachRoot() {
    this.root.removeFromSuperview();
  }
}
